import analytics from "web/script/analytics/analytics";
import classnames from "classnames";
import { gettext } from "web/script/modules/django-i18n";
import PropTypes from "prop-types";
import React from "react";
import environment from "web/script/modules/environment";
import { EU_COUNTRIES } from "../../constants";
import LegalAgreement from "../legal-agreement/legal-agreement";
import Button from "../button/button";
import SocialButton from "../social-button/social-button";
// eslint-disable-next-line css-modules/no-unused-class
import oldStyles from "./email-capture-form.css";
// eslint-disable-next-line css-modules/no-unused-class
import newStyles from "./email-capture-form-new.css";

export function EmailCaptureForm({
    captureType,
    className,
    emailAddress,
    emailLabelText,
    formErrors,
    onEmailChange,
    onLegalChange,
    onSubmit,
    privacyPolicyLink,
    submitText,
    submitClass,
    termsAndConditionsLink,
    cookiePolicyLink,
    shortEUPolicy,
    showSocial,
    onSocialClick,
    newDesign,
    isFBPDP,
}) {
    const useNewDesign = newDesign === true;
    const styles = useNewDesign ? newStyles : oldStyles;
    const country = environment.get("country");
    const isEu = EU_COUNTRIES.has(country);

    function generateErrorDiv(message, key) {
        return (
            <div key={key} className={styles.formItemError}>
                <div dangerouslySetInnerHTML={{ __html: message }} />
            </div>
        );
    }

    let errors = {
        email: undefined,
        legal: undefined,
        other: undefined,
    };
    if (formErrors !== undefined) {
        Object.keys(formErrors).map((key) => {
            if (key === "marketing_emails" || key === "privacy_policy") {
                errors.legal = formErrors[key].map((message, id) => generateErrorDiv(message, id));
            } else if (key === "email") {
                errors.email = formErrors[key].map((message, id) => generateErrorDiv(message, id));
            } else {
                errors.other = formErrors[key].map((message, id) => generateErrorDiv(message, id));
            }
        });
    }

    const social = (
        <div className={styles.socialArea}>
            <span className={styles.orSeparator}>{gettext("in_stock_product.or_separator")}</span>
            <SocialButton
                className={styles.facebookButton}
                type="facebook"
                onClick={() => onSocialClick("facebook")}
                fullWidth
            >
                {gettext("account.register.sign_up_with_facebook")}
            </SocialButton>
        </div>
    );

    const buttonType = useNewDesign ? "primary" : "tertiary";
    const buttons = (
        <div>
            <Button
                styleType={buttonType}
                className={classnames(submitClass, styles.submitButton)}
                type="submit"
                fullWidth
            >
                {submitText}
            </Button>
            {errors.other}
            {showSocial === true && social}
        </div>
    );

    const privacyPolicyLinkProps = `href=${privacyPolicyLink} target="_blank"`;
    const policyText = (
        <div
            className={styles.legalAgreementSmall}
            dangerouslySetInnerHTML={{
                __html: gettext("account.register.terms", {
                    privacy_policy_link_props: privacyPolicyLinkProps,
                }),
            }}
        />
    );

    return (
        <form className={classnames(className, styles.form)} onSubmit={onSubmit}>
            <fieldset className={styles.fieldset}>
                {!useNewDesign && !isFBPDP && (
                    <label className={styles.label} htmlFor="id_email">
                        {emailLabelText || gettext("account.form.email_label")}
                    </label>
                )}
                <input
                    type="email"
                    name="email"
                    value={emailAddress}
                    onClick={() => analytics.event(captureType, "fill_field", "field_email")}
                    className={styles.input}
                    placeholder={gettext("account.form.email_placeholder")}
                    autoCapitalize="off"
                    onChange={onEmailChange}
                    autoFocus
                    required
                />
                {errors.email}
            </fieldset>
            {!isEu && buttons}
            <div className={styles.legalAgreementWrapper}>
                <LegalAgreement
                    className={styles.legalAgreement}
                    country={country}
                    submitText={submitText}
                    handleLegalChange={onLegalChange}
                    captureType={captureType}
                    privacyPolicyLink={privacyPolicyLink}
                    termsAndConditionsLink={termsAndConditionsLink}
                    cookiePolicyLink={cookiePolicyLink}
                    shortEUVersion={shortEUPolicy}
                />
                {errors.legal}
            </div>
            {isEu && buttons}
            {shortEUPolicy && policyText}
        </form>
    );
}

export default EmailCaptureForm;

EmailCaptureForm.propTypes = {
    captureType: PropTypes.string.isRequired,
    className: PropTypes.string,
    cookiePolicyLink: PropTypes.string,
    emailAddress: PropTypes.string,
    emailLabelText: PropTypes.string,
    formErrors: PropTypes.object,
    onEmailChange: PropTypes.func.isRequired,
    onLegalChange: PropTypes.func.isRequired,
    onSubmit: PropTypes.func.isRequired,
    onSocialClick: PropTypes.func,
    showSocial: PropTypes.bool,
    privacyPolicyLink: PropTypes.string.isRequired,
    submitText: PropTypes.string.isRequired,
    submitClass: PropTypes.string,
    termsAndConditionsLink: PropTypes.string,
    shortEUPolicy: PropTypes.bool,
    newDesign: PropTypes.bool,
    isFBPDP: PropTypes.bool,
};
